import { forwardRef, useRef, useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { processWaveform, renderWaveform, getWaveformColors } from '../utils/waveform';

interface VoiceWaveformProps {
  /** Encoded waveform data from Telegram API */
  waveformData?: string;
  /** Duration in seconds */
  duration: number;
  /** Playback progress 0-1 */
  progress: number;
  /** Is outgoing message */
  isOutgoing?: boolean;
  /** Click handler for seeking */
  onSeek?: (progress: number) => void;
  /** Additional className */
  className?: string;
}

const WAVEFORM_HEIGHT = 23;
const SPIKE_STEP = 4;

/**
 * Voice message waveform visualization component
 * Based on Telegram Web A implementation
 */
export const VoiceWaveform = forwardRef<HTMLDivElement, VoiceWaveformProps>(
  ({ waveformData, duration, progress, isOutgoing = false, onSeek, className }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Process waveform data
    const processed = useMemo(
      () => processWaveform(waveformData, duration),
      [waveformData, duration]
    );

    const spikes = useMemo(() => processed.spikes || [], [processed.spikes]);
    const peak = processed.peak || 1;

    // Detect dark mode
    const isDark = useMemo(() => {
      if (typeof window === 'undefined') {return false;}
      return document.documentElement.classList.contains('dark');
    }, []);

    // Get colors
    const colors = useMemo(
      () => getWaveformColors(isOutgoing, isDark),
      [isOutgoing, isDark]
    );

    // Render waveform
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas || spikes.length === 0) {return;}

      renderWaveform(canvas, spikes, peak, progress, colors);
    }, [spikes, peak, progress, colors]);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!onSeek) {return;}

      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const newProgress = Math.max(0, Math.min(1, x / rect.width));
      onSeek(newProgress);
    };

    // Calculate actual width based on spikes
    const waveformWidth = spikes.length * SPIKE_STEP;

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center cursor-pointer touch-none select-none',
          className
        )}
        style={{ height: WAVEFORM_HEIGHT, minWidth: waveformWidth || 100 }}
        onClick={handleClick}
      >
        <canvas
          ref={canvasRef}
          className="block"
        />
      </div>
    );
  }
);

VoiceWaveform.displayName = 'VoiceWaveform';
